import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import { PHONE_SEARCH_PARAM, PHONE_VIEW, PHONE_VIEW_PARAM, ROUTES } from '@lib/routes';
import type { FulfillmentJob } from '@/services';
import type { ListPage } from '@/services/list-envelope';
import { useRouteEta } from '../hooks/useRouteEta';
import { jobStatusOptions } from '../jobResource';
import {
  PHONE_SEARCH_MIN_CHARS,
  buildPhoneSearchParams,
  etaByTaskId,
  monthDay,
  phoneTail,
  splitByDay,
  type SearchEta,
} from './phoneSearchModel';

const DASH = '—';

interface PhoneSearchProps {
  today: string;
  timezone: string | undefined;
}

/**
 * The phone search view (#2678): a caller gives an order number, a name or a
 * phone, the operator finds the delivery job. Submit-only (no debounce): the
 * needle rides the URL (`_q`), so a return from the job detail shows the same
 * results. Today and later only, in two groups; today's rows carry the live
 * ETA, joined on the Onfleet task id.
 */
export function PhoneSearch({ today, timezone }: PhoneSearchProps) {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const needle = searchParams.get(PHONE_SEARCH_PARAM)?.trim() ?? '';
  const ready = needle.length >= PHONE_SEARCH_MIN_CHARS;

  const query = useQuery({
    queryKey: queryKeys.dispatchJobs.list({ scope: 'phone-search', search: needle, from: today }),
    queryFn: () =>
      http.get<ListPage<FulfillmentJob>>('/api/fulfillment-jobs', {
        params: buildPhoneSearchParams(needle, today),
      }),
    enabled: ready,
  });
  const items = query.data?.items ?? [];
  const groups = splitByDay(items, today);
  const eta = useRouteEta(groups.today.some((row) => row.onfleet_task_id !== null));
  const etas = etaByTaskId(eta.data?.date === today ? eta.data : undefined);

  const clock = (ms: number) =>
    new Intl.DateTimeFormat(i18n.language, {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(ms));

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get(PHONE_SEARCH_PARAM);
    setSearchParams(
      {
        [PHONE_VIEW_PARAM]: PHONE_VIEW.search,
        [PHONE_SEARCH_PARAM]: typeof value === 'string' ? value.trim() : '',
      },
      { replace: true },
    );
  };

  let status: string | null = null;
  if (!ready) status = t('search.minChars');
  else if (query.isPending) status = t('search.loading');
  else if (query.isError) status = t('error.loadFailed');
  else if (groups.today.length === 0 && groups.later.length === 0)
    status = t('dispatch.phone.search.empty');
  const truncated =
    ready && query.data && query.data.total > items.length
      ? t('dispatch.phone.search.truncated', { shown: items.length, total: query.data.total })
      : null;

  return (
    <div data-testid="phone-search" className="flex flex-col gap-3">
      <div className="flex items-center gap-1">
        <Link
          to={ROUTES.dispatch.root}
          aria-label={t('dispatch.phone.search.back')}
          className="-ml-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-foreground"
        >
          <ChevronLeft aria-hidden className="size-5" />
        </Link>
        <form role="search" onSubmit={onSubmit} className="min-w-0 flex-1">
          <input
            type="search"
            name={PHONE_SEARCH_PARAM}
            enterKeyHint="search"
            // The view exists to type into: the entry tap is the intent.
            autoFocus
            defaultValue={needle}
            aria-label={t('dispatch.phone.search.inputLabel')}
            placeholder={t('dispatch.phone.search.entry')}
            className="h-11 w-full rounded-lg border border-border bg-card px-3 text-base text-foreground"
          />
        </form>
      </div>
      {status && <p className="m-0 text-muted-foreground">{status}</p>}
      {truncated && <p className="m-0 text-[12.5px] text-muted-foreground">{truncated}</p>}
      {groups.today.length > 0 && (
        <Group title={t('dispatch.phone.search.today', { count: groups.today.length })}>
          {groups.today.map((row) => {
            const rowEta = row.onfleet_task_id ? etas.get(row.onfleet_task_id) : undefined;
            return <Row key={row.id} row={row} when={etaText(rowEta, clock)} />;
          })}
        </Group>
      )}
      {groups.later.length > 0 && (
        <Group title={t('dispatch.phone.search.later', { count: groups.later.length })}>
          {groups.later.map((row) => (
            <Row key={row.id} row={row} when={monthDay(row.shipping_date)} />
          ))}
        </Group>
      )}
    </div>
  );
}

function etaText(eta: SearchEta | undefined, clock: (ms: number) => string): string {
  return eta?.ms == null ? DASH : clock(eta.ms);
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="m-0 text-[12.5px] font-medium text-muted-foreground">{title}</h3>
      <div className="rounded-xl bg-card outline outline-1 -outline-offset-1 outline-border/50">
        {children}
      </div>
    </section>
  );
}

function Row({ row, when }: { row: FulfillmentJob; when: string }) {
  const { t } = useTranslation();
  const tail = phoneTail(row.customer_phone);
  const sub = [
    row.customer_name,
    tail && t('dispatch.phone.search.phoneTail', { tail }),
    row.driver_name,
  ]
    .filter(Boolean)
    .join(' · ');
  const statusLabel = jobStatusOptions.find((option) => option.value === row.status)?.label;
  return (
    <Link
      to={`${ROUTES.dispatch.jobs}/${encodeURIComponent(row.id)}`}
      data-testid={`phone-search-row-${row.id}`}
      className="grid min-h-[60px] grid-cols-[minmax(0,1fr)_auto_18px] items-center gap-x-2.5 border-t border-border/50 px-3.5 py-2.5 text-foreground no-underline first:border-t-0"
    >
      <span className="truncate font-mono font-medium">
        {row.shopify_order_name ?? row.name ?? DASH}
      </span>
      <span className="text-right font-mono text-[12.5px]">{when}</span>
      <ChevronRight aria-hidden className="row-span-2 size-4 text-muted-foreground" />
      <span className="truncate text-[12.5px] text-muted-foreground">{sub || DASH}</span>
      <span className="text-right text-[12.5px] text-muted-foreground">
        {t(statusLabel ?? row.status)}
      </span>
    </Link>
  );
}
